"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { API_BASE_URL } from "@/lib/api"
import { useAuth } from "@/context/AuthContext"

interface CreateUserProps {
  onUserCreated?: () => void
}

export function CreateUser({ onUserCreated }: CreateUserProps) {
  const [form, setForm] = useState({
    userId: "",
    name: "",
    pin: "",
    role: "USER",
    // referralCode is automatically set from current user
  })
  const [loading, setLoading] = useState(false)
  const { user: currentUser } = useAuth()
  const { toast } = useToast()

  const getAvailableRoles = () => {
    if (!currentUser) return []
    
    switch (currentUser.role) {
      case 'ADMIN': return ['DISTRIBUTOR', 'AGENT', 'USER']
      case 'DISTRIBUTOR': return ['AGENT', 'USER']
      case 'AGENT': return ['USER']
      default: return []
    }
  }

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'DISTRIBUTOR': return 'Create a distributor who can manage agents'
      case 'AGENT': return 'Create an agent who can register customers'
      case 'USER': return 'Create a customer who can play lottery'
      default: return ''
    }
  }

  const getHierarchyInfo = () => {
    if (!currentUser) return ""
    
    switch (currentUser.role) {
      case 'ADMIN': 
        return `New ${form.role.toLowerCase()} will be under your hierarchy (Referral: ${currentUser.referralCode})`
      case 'DISTRIBUTOR':
        return `New ${form.role.toLowerCase()} will be under your hierarchy (Referral: ${currentUser.referralCode})`
      case 'AGENT':
        return `New customer will be under your hierarchy (Referral: ${currentUser.referralCode})`
      default:
        return ""
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (form.pin.length < 4) {
      toast({
        title: "Error",
        description: "PIN must be at least 4 digits",
        variant: "destructive",
      })
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('auth_token')
      
      // Prepare data with current user's referral code
      const requestData = {
        ...form,
        referralCode: currentUser?.referralCode // Automatically use current user's referral code
      }

      const response = await fetch(`${API_BASE_URL}/users/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      })

      const data = await response.json()

      if (response.ok) {
        toast({
          title: "Success",
          description: data.message || `${form.role} created successfully`,
          variant: "default",
        })
        setForm({ userId: "", name: "", pin: "", role: "USER" })
        onUserCreated?.()
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to create user",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Error creating user:', error)
      toast({
        title: "Error",
        description: "Failed to create user",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const availableRoles = getAvailableRoles()
  const hierarchyInfo = getHierarchyInfo()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New User</CardTitle>
        <CardDescription>
          {currentUser?.role === 'ADMIN' && 'Create distributors, agents, or customers in your hierarchy'}
          {currentUser?.role === 'DISTRIBUTOR' && 'Create agents or customers in your hierarchy'}
          {currentUser?.role === 'AGENT' && 'Create customers in your hierarchy'}
          {!currentUser && 'Please login to create users'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="userId">User ID</Label>
            <Input
              id="userId"
              value={form.userId}
              onChange={(e) => setForm({...form, userId: e.target.value})}
              placeholder="Enter user ID"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              placeholder="Enter full name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="pin">PIN</Label>
            <Input
              id="pin"
              type="password"
              value={form.pin}
              onChange={(e) => setForm({...form, pin: e.target.value})}
              placeholder="Enter 4-digit PIN"
              maxLength={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={form.role}
              onValueChange={(value) => setForm({...form, role: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map(role => (
                  <SelectItem key={role} value={role}>
                    <div className="flex flex-col">
                      <span className="font-medium">{role}</span>
                      <span className="text-xs text-muted-foreground">
                        {getRoleDescription(role)}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hierarchy Information */}
          {hierarchyInfo && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">{hierarchyInfo}</p>
            </div>
          )}

          {/* Current User Info */}
          {currentUser && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-sm text-gray-600">
                <strong>Your Role:</strong> {currentUser.role} | 
                <strong> Your Referral Code:</strong> {currentUser.referralCode}
              </p>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={loading || availableRoles.length === 0 || !currentUser}
          >
            {loading ? "Creating..." : `Create ${form.role}`}
          </Button>

          {availableRoles.length === 0 && currentUser && (
            <p className="text-sm text-muted-foreground text-center">
              You don't have permission to create users
            </p>
          )}

          {!currentUser && (
            <p className="text-sm text-muted-foreground text-center">
              Please login to create users
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  )
}